import { LitElement, html, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { customElement } from 'lit/decorators.js';
import '../icon/icon.element';
import { selectStyles } from './select.styles';
import { styleMap } from 'lit/directives/style-map.js';

/**
 * Select
 *
 * @prop {boolean} disabled - If select is disabled.
 * @prop {boolean} readonly - If select is readonly.
 * @prop {boolean} required - If select is required.
 * @prop {string} placeholder - Sets the placeholder for select
 * @prop {T | null} selected - The currently selected item
 * @prop {boolean} autocomplete - Enable autocomplete/search functionality
 * @prop {boolean} clearable - Show clear button when item is selected
 *
 * @fires change - Fires when the selection is changed. Detail contains the selected item or null.
 * @fires focus - Fires when the select receives focus
 * @fires blur - Fires when the select loses focus
 */
@customElement('cds-select')
export class SelectElement<T> extends LitElement {
  static styles: CSSResultGroup = selectStyles;

  @property({ type: Array }) items: T[] = [];
  @property({ type: String }) placeholder = 'Select...';
  @property({ type: Boolean }) clearable = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) autocomplete = false;
  @property({ type: String }) noItemsLabel = 'No search result';
  @property({ type: Boolean }) isLoading = false;
  @property({ type: Boolean }) isError = false;
  @property({ type: String }) loadingLabel = 'Loading...';
  @property({ type: String }) loadingErrorLabel = 'Error loading items';
  @property({ type: Function }) valueFn?: (item: T) => string;
  @property({ type: Function }) filterFn?: (item: T, searchTerm: string) => boolean;

  private _selected: T | null = null;

  @property({ type: Object })
  get selected(): T | null {
    return this._selected;
  }
  set selected(val: T | null) {
    const oldVal = this._selected;
    this._selected = val;
    this.requestUpdate('selected', oldVal);
  }

  @state() private open = false;
  @state() private searchTerm = '';
  @state() private dropdownAbove = false;
  @state() private blurred = false;
  @state() private highlightedIndex = -1;
  @state() private dropdownStyles: { [key: string]: string } = {};

  @query('input') private inputElement!: HTMLInputElement;

  // Bound event handlers for proper cleanup
  private resizeHandler = () => this.updateDropdownPosition();
  private scrollHandler = () => this.updateDropdownPosition();
  private clickOutsideHandler = this.handleClickOutside.bind(this);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('scroll', this.scrollHandler, true);
    
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget as Node)) {
        this.classList.remove('focus');
        if (this.inputElement) {
          this.inputElement.value = this.selected
            ? this.getItemLabel(this.selected)
            : '';
        }
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('scroll', this.scrollHandler, true);
    this.removeEventListeners();
  }

  get filteredItems(): T[] {
    if (!this.searchTerm) return this.items;
    
    if (this.filterFn) {
      return this.items?.filter((item) => this.filterFn!(item, this.searchTerm)) || [];
    }
    
    return this.items?.filter((item) =>
      this.getItemLabel(item)
        .toLowerCase()
        .includes(this.searchTerm.toLowerCase())
    ) || [];
  }

  get showError(): boolean {
    return this.required && this.blurred && !this.selected && !this.searchTerm;
  }

  updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    
    if (changedProperties.has('open')) {
      if (this.open) {
        this.addEventListeners();
        // Recalculate position when dropdown opens
        this.updateDropdownPosition();
      } else {
        this.removeEventListeners();
      }
    }
    
    if (changedProperties.has('selected') && this.inputElement) {
      this.inputElement.value = this.selected ? this.getItemLabel(this.selected) : '';
    }
    
    // Recalculate position when filtered items change during search
    if (changedProperties.has('searchTerm') && this.open) {
      this.updateComplete.then(() => this.updateDropdownPosition());
    }
  }

  private addEventListeners(): void {
    document.addEventListener('click', this.clickOutsideHandler);
  }

  private removeEventListeners(): void {
    document.removeEventListener('click', this.clickOutsideHandler);
  }

  private clearSelection(e: Event): void {
    e.stopPropagation();
    e.preventDefault();
    
    const hadSelection = this.selected !== null;
    this.selected = null;
    this.searchTerm = '';
    this.inputElement.value = '';
    
    if (hadSelection) {
      this.dispatchEvent(new CustomEvent('change', { detail: null }));
    }
    
    // Keep dropdown open after clearing if autocomplete is enabled
    if (this.autocomplete && !this.open) {
      this.updateComplete.then(() => {
        this.openDropdownWithHighlight();
      });
    }
  }

  private closeDropdown(): void {
    this.open = false;
    this.searchTerm = '';
    
    if (this.inputElement) {
      this.inputElement.value = this.selected
        ? this.getItemLabel(this.selected)
        : '';
      this.inputElement.blur();
    }
    
    this.dispatchEvent(new CustomEvent('blur'));
    this.blurred = true;
  }

  private getItemLabel(item: T): string {
    if (this.valueFn) {
      return this.valueFn(item);
    }
    
    if (typeof item === 'object' && item !== null) {
      console.warn('valueFn should be provided when items are objects');
      return String(item);
    }
    
    return String(item);
  }

  private handleClickOutside(e: Event): void {
    const path = e.composedPath?.() || [];
    if (this.open && !path.includes(this)) {
      this.closeDropdown();
    }
  }

  private onFocus(): void {
    this.classList.add('focus');
    
    if (this.autocomplete && this.inputElement) {
      this.inputElement.removeAttribute('readonly');
      this.inputElement.value = '';
      this.inputElement.tabIndex = 0;
      this.inputElement.focus();
    }
  }

  private onSearch(e: InputEvent): void {
    const target = e.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.open = true;
    
    // Reset highlighted index when searching
    this.highlightedIndex = 0;
  }

  private onSelect(item: T): void {
    const changed = !this.areItemsEqual(this.selected, item);
    this.selected = item;
    this.open = false;
    this.searchTerm = '';
    this.updateComplete.then(() => {
      this.inputElement.value = this.getItemLabel(item);
    });
    if (changed) {
      this.dispatchEvent(new CustomEvent('change', { detail: item }));
    }
  }

  private updateDropdownPosition(): void {
    if (!this.open) return;

    const dropdown = this.renderRoot.querySelector('.dropdown-list') as HTMLElement;
    if (!dropdown) {
      requestAnimationFrame(() => this.updateDropdownPosition());
      return;
    }

    const selectRect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Constants
    const DROPDOWN_MAX_HEIGHT = 300;
    const MARGIN_BUFFER = 8;
    
    // Calculate available space
    const spaceBelow = viewportHeight - selectRect.bottom - MARGIN_BUFFER;
    const spaceAbove = selectRect.top - MARGIN_BUFFER;
    
    // Determine if dropdown should show above or below
    const shouldShowAbove = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
    
    // Calculate actual max height based on available space
    const maxHeight = shouldShowAbove
      ? Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove)
      : Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow);
    
    // Build styles for fixed positioning
    const styles: { [key: string]: string } = {
      position: 'fixed',
      width: `${selectRect.width}px`,
      maxHeight: `${maxHeight}px`,
      left: `${Math.max(0, Math.min(selectRect.left, viewportWidth - selectRect.width))}px`,
      zIndex: '1000',
    };
    
    if (shouldShowAbove) {
      styles.bottom = `${viewportHeight - selectRect.top}px`;
      styles.top = 'auto';
    } else {
      styles.top = `${selectRect.bottom}px`;
      styles.bottom = 'auto';
    }
    
    // Only update if changed to prevent unnecessary re-renders
    const stylesChanged = JSON.stringify(this.dropdownStyles) !== JSON.stringify(styles);
    const positionChanged = this.dropdownAbove !== shouldShowAbove;
    
    if (stylesChanged || positionChanged) {
      this.dropdownAbove = shouldShowAbove;
      this.dropdownStyles = styles;
      this.requestUpdate();

      // Scroll to highlighted item after position change
      this.updateComplete.then(() => {
        this.scrollToHighlightedItem();
      });
    }
  }

  private openDropdownWithHighlight(): void {
    this.open = true;
    
    // Find the selected item's index in filtered items
    const selectedIdx = this.selected
      ? this.filteredItems.findIndex(item => this.areItemsEqual(item, this.selected))
      : -1;
    
    this.highlightedIndex = selectedIdx >= 0 ? selectedIdx : 0;
    
    if (this.autocomplete && this.inputElement) {
      this.inputElement.focus();
    }
    
    // Calculate position and scroll to highlighted item
    requestAnimationFrame(() => {
      this.updateDropdownPosition();
      
      this.updateComplete.then(() => {
        this.scrollToHighlightedItem();
      });
    });
  }

  private scrollToHighlightedItem(): void {
    const list = this.renderRoot.querySelector('.dropdown-list');
    if (!list || this.highlightedIndex < 0) return;
    
    const el = list.children[this.highlightedIndex] as HTMLElement;
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      });
    }
  }

  private toggleDropdown(): void {
    if (this.disabled) return;
    
    if (!this.open) {
      this.openDropdownWithHighlight();
      this.searchTerm = '';
      this.inputElement.value = this.autocomplete ? '' : this.selected ? this.getItemLabel(this.selected) : '';
      this.dispatchEvent(new CustomEvent('focus'));
    } else {
      this.closeDropdown();
    }
  }

  private onKeyDown(e: KeyboardEvent): void {
    // Open dropdown on arrow keys, space, or enter
    if (!this.open && ['ArrowDown', 'ArrowUp', ' ', 'Enter'].includes(e.key)) {
      this.openDropdownWithHighlight();
      e.preventDefault();
      return;
    }
    
    if (!this.open || this.filteredItems.length === 0) return;
    
    const items = this.filteredItems;
    
    switch (e.key) {
      case 'ArrowDown':
        this.highlightedIndex = (this.highlightedIndex + 1) % items.length;
        e.preventDefault();
        break;
        
      case 'ArrowUp':
        this.highlightedIndex = (this.highlightedIndex - 1 + items.length) % items.length;
        e.preventDefault();
        break;
        
      case 'Enter':
      case ' ':
        if (this.highlightedIndex >= 0 && this.highlightedIndex < items.length) {
          this.onSelect(items[this.highlightedIndex]);
        }
        e.preventDefault();
        break;
        
      case 'Escape':
        this.closeDropdown();
        e.preventDefault();
        break;
        
      case 'Tab':
        this.closeDropdown();
        // Don't prevent default to allow normal tab navigation
        break;
        
      case 'Home':
        this.highlightedIndex = 0;
        e.preventDefault();
        break;
        
      case 'End':
        this.highlightedIndex = items.length - 1;
        e.preventDefault();
        break;
    }
    // Calculate position and scroll to highlighted item
    requestAnimationFrame(() => {
      this.updateDropdownPosition();
      
      this.updateComplete.then(() => {
        this.scrollToHighlightedItem();
      });
    });
  }

  // Helper to compare items. If the items are objects, uses valueFn for comparison.
  // otherwise, does direct string comparison.
  private areItemsEqual(a: T | null, b: T | null): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    
    if (this.valueFn) {
      return this.valueFn(a) === this.valueFn(b);
    }
    
    return a === b;
  }

  render() {
    const clearIconStyles = styleMap({
      display:
        this.clearable && !this.disabled && (this.searchTerm || this.selected)
          ? 'block'
          : 'none',
      pointerEvents: this.open ? 'none' : 'auto',
    });
    
    // Update error state
    if (this.showError) {
      this.classList.add('error');
    } else {
      this.classList.remove('error');
    }

    const loadingTemplate = html`
      <li class="dropdown-item loading">
        <cds-button
          processing
          variant="link"
          class="icon"
          size="small"
        ></cds-button>
        ${this.loadingLabel
          ? html`<span class="label">${this.loadingLabel}</span>`
          : null}
      </li>
    `;

    const errorLoadingTemplate = html`
      <li class="dropdown-item error">
        <cds-icon icon="status-failure" size="s"></cds-icon>
        ${this.loadingErrorLabel
          ? html`<span class="label">${this.loadingErrorLabel}</span>`
          : null}
      </li>
    `;

    return this.readonly
      ? html`<span class="readonly-container">
          ${this.selected ? this.getItemLabel(this.selected) : ''}
        </span>`
      : html`
          <div class="cds-select">
            <div
              class="input-wrapper"
              style=${this.open ? 'cursor: default' : ''}
              @mousedown=${(e: MouseEvent) => {
                e.stopPropagation();
                this.toggleDropdown();
              }}
              @focus=${this.onFocus}
              @keydown=${this.onKeyDown}
              tabindex="0"
            >
              <input
                class="search-input"
                type="text"
                autocomplete="off"
                placeholder=${this.placeholder}
                ?disabled=${this.disabled}
                ?required=${this.required}
                ?readonly=${!this.autocomplete || !this.open}
                .value=${this.selected ? this.getItemLabel(this.selected) : ''}
                tabindex=${this.autocomplete && this.open ? '0' : '-1'}
                style=${!this.autocomplete
                  ? 'pointer-events: none; caret-color: transparent;'
                  : this.autocomplete && this.open
                    ? 'cursor: text;'
                    : ''}
                @input=${this.autocomplete ? this.onSearch : null}
                aria-expanded=${this.open}
                aria-haspopup="listbox"
                role="combobox"
              />
              <cds-icon
                class="cds-select--icon right"
                icon="close"
                tabindex="-1"
                aria-label="Clear"
                size="s"
                style=${clearIconStyles}
                @mousedown=${this.clearSelection}
              ></cds-icon>
              <cds-icon
                class="cds-select--icon right"
                icon=${this.open ? 'chevron-up' : 'chevron-down'}
                aria-label="Toggle dropdown"
                style="pointer-events: none;"
                size="m"
              ></cds-icon>
            </div>
            ${this.open
              ? html`
                  <ul
                    class="dropdown-list ${this.dropdownAbove
                      ? 'dropdown-above'
                      : 'dropdown-below'}"
                    role="listbox"
                    style=${styleMap(this.dropdownStyles)}
                  >
                    ${this.isLoading
                      ? loadingTemplate
                      : this.filteredItems.length === 0
                        ? this.isError
                          ? errorLoadingTemplate
                          : html`<li class="dropdown-item no-items" disabled>
                              ${this.noItemsLabel}
                            </li>`
                        : this.filteredItems.map(
                            (item, idx) => html`
                              <li
                                class="dropdown-item 
                                  ${idx === this.highlightedIndex ? 'highlighted' : ''}
                                  ${this.areItemsEqual(item, this.selected) ? 'selected' : ''}"
                                role="option"
                                aria-selected=${this.areItemsEqual(item, this.selected)}
                                @mousedown=${(e: Event) => {
                                  e.preventDefault();
                                  this.onSelect(item);
                                }}
                                @mouseenter=${() => {
                                  this.highlightedIndex = idx;
                                }}
                              >
                                ${this.getItemLabel(item)}
                              </li>
                            `
                          )}
                  </ul>
                `
              : null}
          </div>
        `;
  }
}
